import {Component, Prop, h, Element, Event, EventEmitter, Host, Method} from '@stencil/core';

/**
 * If you need to get the actual state of the component, you can simply do a :
 * document.querySelector('joy-checkbox.this-instance').checked
 */
@Component({
    tag: 'joy-checkbox',
    styleUrl: './style/checkbox.scss',
    scoped: true,
})
export class Checkbox {
    @Element() el!: HTMLJoyCheckboxElement;
    private input!: HTMLInputElement;

    /** Input value. Input value != checked state ! If you need to get the state checked/not checked, simply use checked prop */
    @Prop({reflect: true}) value = 'on'; // On is the native value of a checkbox

    /** Input name for form participation */
    @Prop({reflect: true}) name = '';

    /** Checkbox is required */
    @Prop({reflect: true}) required = false;

    /** Disabled state */
    @Prop({reflect: true}) disabled = false;

    /** Checkbox activated or not */
    @Prop({mutable: true, reflect: true}) checked = false;

    /** If `true`, the checkbox will visually appear as indeterminate. */
    @Prop({mutable: true}) indeterminate = false;

    /** If `true`, the checkbox will visually appear as invalid. */
    @Prop({mutable: true}) invalid = false;

    /** Clicking on the component will fire this customEvent */
    @Event({composed: true, eventName: 'value-change'}) valueChange!: EventEmitter<boolean>;

    /** Clicking on the component will fire this customEvent (more checkbox specific event) */
    @Event({composed: true, eventName: 'joy-checkbox-change'}) joyCheckboxChange!: EventEmitter<boolean>;

    /**
     * Update checkbox state from outside the component
     * @param {Boolean} newValue
     */
    @Method()
    async updateValue(newValue: boolean): Promise<void> {
        this.checked = newValue;
    }

    private onChange = () => {
        if (!this.disabled) {
            this.checked = !this.checked;
            this.indeterminate = false;
            this.invalid = false;
            this.valueChange.emit(this.checked);
            this.joyCheckboxChange.emit(this.checked);
        }
    };

    private isIndeterminate() {
        /** In the case both "checked" and "indeterminate" props are set to true, checked wins. */
        return this.indeterminate && !this.checked;
    }

    componentDidLoad() {
        this.input.indeterminate = this.isIndeterminate();
    }

    render() {
        return (
            <Host aria-checked={`${this.checked}`} aria-hidden={this.disabled ? 'true' : null}>
                <label
                    class={{
                        'joy-checkbox': true,
                        'joy-checkbox__checked': this.checked,
                        'joy-checkbox__disabled': this.disabled,
                        'joy-checkbox__invalid': this.invalid,
                        'joy-checkbox__indeterminate': this.isIndeterminate(),
                    }}
                >
                    <input
                        type="checkbox"
                        ref={(el) => (this.input = el as HTMLInputElement)}
                        class="joy-checkbox__input"
                        role="checkbox"
                        name={this.name}
                        onChange={this.onChange}
                        disabled={this.disabled}
                        checked={this.checked}
                        required={this.required}
                        aria-checked={`${this.checked}`}
                        value={this.value}
                    />
                    <div class="joy-checkbox__content-wrapper">
                        <slot />
                        <div class="joy-checkbox__content">
                            <slot name="checkbox-content" />
                        </div>
                    </div>
                </label>
            </Host>
        );
    }
}
